import { NullFunction } from '@deqo/foundation';
import React, { FC, useEffect, useReducer } from 'react';
import { useResolve } from '../../app/Container';
import { ResourceManager } from '../resources/ResourceManager';

const loadCache = new Map<string, any[]>();

interface ImageState { type?: 'img' | 'svg' | 'unknown', value?: any, transform?: [number, number, number] }
type ImageAction = ['reset'] | ['set', ImageState];
type ImageProps = React.ImgHTMLAttributes<HTMLImageElement>;

const imageReducer = (state: ImageState, [type, p1]: ImageAction) => {
    if (type === 'reset') return {};
    if (type === 'set') return p1!;
    throw new Error(`reducer failure: ${type} is not an action type`);
};

export type Image = FC<ImageProps>;
export const Image: Image = (props) => {

    const [r, dispatch] = useReducer(imageReducer, {});
    const resources = useResolve(ResourceManager);
    const { type, value, transform } = r;

    useEffect(() => void (doLoad(resources, props.src!, dispatch)), [props.src, resources]);

    const styling = Object.assign({}, props.style ?? {}, !transform ? {} : {
        'transform': `rotate(${transform[2]}deg) scale(${transform[0]}, ${transform[1]})`
    });

    if (type === 'img') return <img {...props} style={styling} src={value} alt={props.alt} />;
    if (type === 'svg') return <SvgFromDocument {...props} style={styling} document={value} />;
    return <></>;
};

export const preLoadSvgImage = async (resources: ResourceManager, src: string) => {
    await doLoad(resources, src, NullFunction);
};

export const preBuildSvgCache = (path: string, svgData: string) => {
    const type = 'svg';
    const value = parse(svgData);
    loadCache.set(path, [type, value]);
};

const doLoad = async (resources: ResourceManager, src: string, dispatch: React.Dispatch<ImageAction>) => {

    dispatch(['reset']);

    let type = 'unknown' as any;
    let value = null as any;
    let transform = null as any;

    const [uri, transformExpr] = src.split('@');
    const cacheEntry = loadCache.get(uri);

    if (cacheEntry) {
        type = cacheEntry[0];
        value = cacheEntry[1];
    }
    else if (uri.startsWith('deqo://')) {
        const info = await resources.info(uri);
        if (info.type === 'image/svg+xml') {
            type = 'svg';
            value = parse(await resources.fetch(info.path, 'image/svg+xml'));
        }
        else {
            type = 'img';
            value = info.path;
        }
    }
    else if (uri.endsWith('.svg')) {
        type = 'svg';
        value = parse(await fetch(uri).then(x => x.text()));
    }
    else {
        type = 'img';
        value = src;
    }

    transform = !!transformExpr ? JSON.parse(transformExpr) : null;

    if (!cacheEntry) {
        loadCache.set(uri, [type, value]);
    }

    dispatch(['set', { type, value, transform }]);
};

class SvgFromDocument extends React.Component<ImageProps & { document: Document }> {

    private get attrs() {
        return attrs(this.props.document.documentElement, 'width', 'height', 'viewBox');
    }

    private get innerHTML() {
        return this.props.document.documentElement.innerHTML;
    }

    public render() {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { document, src, ...imageProps } = this.props;
        return <svg {...imageProps} {...this.attrs} dangerouslySetInnerHTML={{ __html: this.innerHTML }} />;
    }
}

const attrs = (elmt: Element, ...list: string[]) => {
    return Object.fromEntries(list.map(x => [x, (elmt.attributes.getNamedItem(x) || {} as any).value]).filter(x => !!x[1]));
};

const parse = (svg: string) => {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svg, 'image/svg+xml');
    const svgScripts = svgDoc.querySelectorAll('script');
    svgScripts.forEach(x => x.parentElement!.removeChild(x));
    return svgDoc;
};
